import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Phone, ArrowRight, Shield, UserCog, GraduationCap, Mail, Lock } from "lucide-react";
import api from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1791f9d8-ffce-4632-931c-e01b5c5c94c3/artifacts/sbeyyf6q_1000003487.png";

export default function LoginPage() {
  const [role, setRole] = useState("student");
  const [tab, setTab] = useState("phone");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, updateUser } = useAuth();
  const navigate = useNavigate();

  // Email Login fields
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup fields
  const [signupName, setSignupName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");

  // Phone OTP fields
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpName, setOtpName] = useState("");
  const [demoOtp, setDemoOtp] = useState("");

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setError("");
    if (newRole === "admin") {
      setTab("login");
    } else {
      setTab("phone");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Raw API call first to validate role BEFORE setting user state
      const { data } = await api.post("/auth/login", { email: loginEmail, password: loginPassword });

      // Role mismatch checks — don't set user if role doesn't match
      if (role === "admin" && data.role !== "admin") {
        await api.post("/auth/logout"); // clear httpOnly cookie
        setError("This account is not an admin account.");
        setLoading(false);
        return;
      }
      if (role === "student" && data.role === "admin") {
        await api.post("/auth/logout");
        setError("Admin accounts must use the Admin login.");
        setLoading(false);
        return;
      }

      // Role matches — now safely set token and user
      sessionStorage.setItem("token", data.token);
      updateUser(data);
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(signupName, signupPhone, signupEmail, signupPassword);
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-otp", { phone: otpPhone });
      setOtpSent(true);
      setDemoOtp(data.demo_otp || "");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        phone: otpPhone,
        otp: otpCode,
        name: otpName,
      });
      sessionStorage.setItem("token", data.token);
      updateUser(data);
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const resetOTP = () => {
    setOtpSent(false);
    setOtpCode("");
    setDemoOtp("");
    setError("");
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      {/* Top section */}
      <div className="bg-[#0284C7] px-6 pt-10 pb-8 text-center text-white">
        <img src={LOGO_URL} alt="Logo" className="w-20 h-20 mx-auto mb-3 object-contain" />
        <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Smart English Fast English
        </h1>
        <p className="text-sm text-white/80 mt-1">Competitive Exam Preparation</p>
      </div>

      {/* Role Selector */}
      <div className="flex bg-slate-100 border-b border-slate-200" data-testid="role-selector">
        <button
          data-testid="role-student-btn"
          onClick={() => handleRoleChange("student")}
          className={`flex-1 py-3 text-xs font-bold flex items-center justify-center gap-1.5 transition-none ${
            role === "student"
              ? "bg-white text-[#0284C7] border-b-2 border-[#0284C7] shadow-sm"
              : "text-slate-400 hover:text-slate-500"
          }`}
        >
          <GraduationCap size={15} /> STUDENT
        </button>
        <button
          data-testid="role-admin-btn"
          onClick={() => handleRoleChange("admin")}
          className={`flex-1 py-3 text-xs font-bold flex items-center justify-center gap-1.5 transition-none ${
            role === "admin"
              ? "bg-white text-[#0284C7] border-b-2 border-[#0284C7] shadow-sm"
              : "text-slate-400 hover:text-slate-500"
          }`}
        >
          <UserCog size={15} /> ADMIN
        </button>
      </div>

      {/* Student Tabs - only when student role is selected */}
      {role === "student" && (
        <div className="flex bg-slate-50" data-testid="auth-tabs">
          <button
            data-testid="phone-tab"
            onClick={() => { setTab("phone"); setError(""); }}
            className={`flex-1 py-3.5 text-xs font-bold transition-none flex items-center justify-center gap-1.5 ${
              tab === "phone" ? "text-[#0284C7] border-b-2 border-[#0284C7] bg-white" : "text-slate-400"
            }`}
          >
            <Phone size={14} /> PHONE OTP
          </button>
          <button
            data-testid="login-tab"
            onClick={() => { setTab("login"); setError(""); }}
            className={`flex-1 py-3.5 text-xs font-bold transition-none ${
              tab === "login" ? "text-[#0284C7] border-b-2 border-[#0284C7] bg-white" : "text-slate-400"
            }`}
          >
            EMAIL LOGIN
          </button>
          <button
            data-testid="signup-tab"
            onClick={() => { setTab("signup"); setError(""); }}
            className={`flex-1 py-3.5 text-xs font-bold transition-none ${
              tab === "signup" ? "text-[#0284C7] border-b-2 border-[#0284C7] bg-white" : "text-slate-400"
            }`}
          >
            SIGN UP
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div data-testid="auth-error" className="mx-6 mt-4 bg-red-50 text-red-700 px-4 py-2 text-sm font-medium">
          {error}
        </div>
      )}

      {/* ─── ADMIN LOGIN FORM ─── */}
      {role === "admin" && (
        <form onSubmit={handleLogin} className="p-6 space-y-4" data-testid="admin-login-form">
          <div className="text-center mb-4">
            <div className="w-14 h-14 bg-[#0284C7]/10 flex items-center justify-center mx-auto mb-2 rounded-full">
              <UserCog size={24} className="text-[#0284C7]" />
            </div>
            <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Admin Login
            </h2>
            <p className="text-xs text-slate-500 mt-1">Sign in with your admin email and password</p>
          </div>
          <div className="relative">
            <Mail size={16} className="absolute left-4 top-3.5 text-slate-400" />
            <input
              data-testid="admin-email-input"
              type="email"
              placeholder="Admin Email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              className={`${inputClass} pl-11`}
              required
            />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-4 top-3.5 text-slate-400" />
            <input
              data-testid="admin-password-input"
              type={showPwd ? "text" : "password"}
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              className={`${inputClass} pl-11`}
              required
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-3 text-slate-400">
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#0284C7] text-white font-bold py-4 text-sm transition-none hover:bg-[#0369A1] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? "Logging in..." : <><Lock size={16} /> LOGIN AS ADMIN</>}
          </button>
        </form>
      )}

      {/* ─── STUDENT: Phone OTP Form ─── */}
      {role === "student" && tab === "phone" && (
        <div className="p-6" data-testid="phone-otp-form">
          {!otpSent ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-[#0284C7]/10 flex items-center justify-center mx-auto mb-2">
                  <Phone size={24} className="text-[#0284C7]" />
                </div>
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Login with Phone Number
                </h2>
                <p className="text-xs text-slate-500 mt-1">Enter your phone number to receive OTP</p>
              </div>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-sm text-slate-500 font-medium">+91</span>
                <input
                  data-testid="otp-phone-input"
                  type="tel"
                  placeholder="Enter 10-digit phone number"
                  value={otpPhone}
                  onChange={(e) => setOtpPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className={`${inputClass} pl-14`}
                  maxLength={10}
                  required
                />
              </div>
              <button
                data-testid="send-otp-btn"
                type="submit"
                disabled={loading || otpPhone.length < 10}
                className="w-full bg-[#0284C7] text-white font-bold py-4 text-sm transition-none hover:bg-[#0369A1] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Sending..." : <><ArrowRight size={16} /> Send OTP</>}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-green-50 flex items-center justify-center mx-auto mb-2">
                  <Shield size={24} className="text-green-600" />
                </div>
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Verify OTP
                </h2>
                <p className="text-xs text-slate-500 mt-1">OTP sent to +91 {otpPhone}</p>
              </div>

              {demoOtp && (
                <div className="bg-amber-50 border border-amber-200 p-3 text-center" data-testid="demo-otp-banner">
                  <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Demo Mode</p>
                  <p className="text-2xl font-black text-amber-700 tracking-[0.3em] mt-1">{demoOtp}</p>
                  <p className="text-[10px] text-amber-500 mt-1">Use this OTP to login (SMS not sent in demo)</p>
                </div>
              )}

              <input
                data-testid="otp-code-input"
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={`${inputClass} text-center text-lg tracking-[0.5em] font-bold`}
                maxLength={6}
                required
              />

              <input
                data-testid="otp-name-input"
                type="text"
                placeholder="Your Name (for new accounts)"
                value={otpName}
                onChange={(e) => setOtpName(e.target.value)}
                className={inputClass}
              />

              <button
                data-testid="verify-otp-btn"
                type="submit"
                disabled={loading || otpCode.length < 6}
                className="w-full bg-green-600 text-white font-bold py-4 text-sm transition-none hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Verifying..." : <><Shield size={16} /> Verify & Login</>}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  data-testid="change-phone-btn"
                  onClick={resetOTP}
                  className="text-xs font-bold text-[#0284C7] transition-none"
                >
                  Change Number
                </button>
                <button
                  type="button"
                  data-testid="resend-otp-btn"
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="text-xs font-bold text-slate-500 transition-none"
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ─── STUDENT: Email Login Form ─── */}
      {role === "student" && tab === "login" && (
        <form onSubmit={handleLogin} className="p-6 space-y-4" data-testid="login-form">
          <input
            data-testid="login-email-input"
            type="email"
            placeholder="Email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            className={inputClass}
            required
          />
          <div className="relative">
            <input
              data-testid="login-password-input"
              type={showPwd ? "text" : "password"}
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              className={inputClass}
              required
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-3 text-slate-400">
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#0284C7] text-white font-bold py-4 text-sm transition-none hover:bg-[#0369A1] disabled:opacity-50"
          >
            {loading ? "Logging in..." : "LOGIN"}
          </button>
        </form>
      )}

      {/* ─── STUDENT: Signup Form ─── */}
      {role === "student" && tab === "signup" && (
        <form onSubmit={handleSignup} className="p-6 space-y-4" data-testid="signup-form">
          <input
            data-testid="signup-name-input"
            type="text"
            placeholder="Full Name"
            value={signupName}
            onChange={(e) => setSignupName(e.target.value)}
            className={inputClass}
            required
          />
          <input
            data-testid="signup-phone-input"
            type="tel"
            placeholder="Phone Number"
            value={signupPhone}
            onChange={(e) => setSignupPhone(e.target.value)}
            className={inputClass}
          />
          <input
            data-testid="signup-email-input"
            type="email"
            placeholder="Email"
            value={signupEmail}
            onChange={(e) => setSignupEmail(e.target.value)}
            className={inputClass}
            required
          />
          <div className="relative">
            <input
              data-testid="signup-password-input"
              type={showPwd ? "text" : "password"}
              placeholder="Password"
              value={signupPassword}
              onChange={(e) => setSignupPassword(e.target.value)}
              className={inputClass}
              required
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-3 text-slate-400">
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button
            data-testid="signup-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#0284C7] text-white font-bold py-4 text-sm transition-none hover:bg-[#0369A1] disabled:opacity-50"
          >
            {loading ? "Creating Account..." : "SIGN UP"}
          </button>
        </form>
      )}
    </div>
  );
}
